import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";

import type { ClockStatus } from "../features/countdown";
import { formatJstTime, parseJstDateTimeInput } from "../features/countdown";
import {
  MAX_ANNOUNCEMENT_LENGTH,
  MAX_ANNOUNCEMENTS,
} from "../config/announcements";
import { SLIDE_DEFINITIONS } from "../config/slides";
import type { DisplaySettings, SlideId } from "../types";
import { MAX_PROJECTION_REFRESH_DURATION_MS } from "../../shared/participation-refresh";

interface SettingsDialogProps {
  readonly initialSettings: DisplaySettings;
  readonly canCancel: boolean;
  readonly nowEpochMs: number;
  readonly clockStatus: ClockStatus;
  readonly systemNotices: readonly string[];
  readonly showParticipationRefreshAction: boolean;
  readonly participationRefreshing: boolean;
  readonly onApiIdChange: (apiId: DisplaySettings["apiId"]) => void;
  readonly onParticipationEnabledChange: (enabled: boolean) => void;
  readonly onRefreshParticipation: (
    apiId: DisplaySettings["apiId"],
    targetEpochMs: number,
  ) => void;
  readonly onComplete: (settings: DisplaySettings) => void;
  readonly onCancel: () => void;
}

export function SettingsDialog({
  initialSettings,
  canCancel,
  nowEpochMs,
  clockStatus,
  systemNotices,
  showParticipationRefreshAction,
  participationRefreshing,
  onApiIdChange,
  onParticipationEnabledChange,
  onRefreshParticipation,
  onComplete,
  onCancel,
}: SettingsDialogProps) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const [startAtJst, setStartAtJst] = useState(initialSettings.startAtJst);
  const [apiId, setApiId] = useState(initialSettings.apiId);
  const [enabledSlides, setEnabledSlides] = useState<readonly SlideId[]>(
    initialSettings.enabledSlides,
  );
  const [announcements, setAnnouncements] = useState<readonly string[]>(
    initialSettings.announcements,
  );
  const [requestFullscreen, setRequestFullscreen] = useState(
    initialSettings.requestFullscreen,
  );
  const [countdownSoundEnabled, setCountdownSoundEnabled] = useState(
    initialSettings.countdownSoundEnabled,
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    if (typeof dialog.showModal === "function") {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }

    const handleCancel = (event: Event): void => {
      event.preventDefault();
      if (canCancel) onCancelRef.current();
    };
    dialog.addEventListener("cancel", handleCancel);

    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      if (dialog.open && typeof dialog.close === "function") dialog.close();
    };
  }, [canCancel]);

  const targetSummary = useMemo(() => {
    try {
      const target = parseJstDateTimeInput(startAtJst);
      const seconds = Math.ceil((target - nowEpochMs) / 1_000);
      if (seconds <= 0) {
        return "開始時刻を過ぎています。現在より後の時刻を指定してください";
      }
      const minutes = Math.floor(seconds / 60);
      const remainder = seconds % 60;
      return `開始まで約 ${minutes}分${String(remainder).padStart(2, "0")}秒`;
    } catch {
      return "有効なJST日時を入力してください";
    }
  }, [nowEpochMs, startAtJst]);
  const manualRefreshTargetEpochMs = useMemo(() => {
    try {
      return parseJstDateTimeInput(startAtJst);
    } catch {
      return null;
    }
  }, [startAtJst]);
  const manualRefreshTargetIsValid = manualRefreshTargetEpochMs !== null
    && manualRefreshTargetEpochMs > nowEpochMs
    && manualRefreshTargetEpochMs - nowEpochMs
      <= MAX_PROJECTION_REFRESH_DURATION_MS;
  const startAtIsFuture = useMemo(() => {
    try {
      return parseJstDateTimeInput(startAtJst) > nowEpochMs;
    } catch {
      return false;
    }
  }, [nowEpochMs, startAtJst]);

  const toggleSlide = (slideId: SlideId): void => {
    const nextEnabledSlides = enabledSlides.includes(slideId)
      ? enabledSlides.filter((id) => id !== slideId)
      : [...enabledSlides, slideId];
    setEnabledSlides(nextEnabledSlides);
    if (slideId === "participation") {
      onParticipationEnabledChange(nextEnabledSlides.includes("participation"));
    }
  };

  const moveSlide = (slideId: SlideId, offset: -1 | 1): void => {
    setEnabledSlides((current) => {
      const index = current.indexOf(slideId);
      const nextIndex = index + offset;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      const other = next[nextIndex];
      if (!other) return current;
      next[index] = other;
      next[nextIndex] = slideId;
      return next;
    });
  };

  const updateAnnouncement = (index: number, value: string): void => {
    setAnnouncements((current) =>
      current.map((announcement, currentIndex) =>
        currentIndex === index ? value : announcement,
      ),
    );
  };

  const addAnnouncement = (): void => {
    setAnnouncements((current) =>
      current.length >= MAX_ANNOUNCEMENTS ? current : [...current, ""],
    );
  };

  const removeAnnouncement = (index: number): void => {
    setAnnouncements((current) =>
      current.filter((_announcement, currentIndex) => currentIndex !== index),
    );
  };

  const orderedDefinitions = [
    ...enabledSlides.flatMap((id) => {
      const definition = SLIDE_DEFINITIONS.find((slide) => slide.id === id);
      return definition ? [definition] : [];
    }),
    ...SLIDE_DEFINITIONS.filter((slide) => !enabledSlides.includes(slide.id)),
  ];

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (enabledSlides.length === 0) {
      setValidationError("少なくとも1つのスライドを有効にしてください。");
      return;
    }

    let targetEpochMs: number;
    try {
      targetEpochMs = parseJstDateTimeInput(startAtJst);
    } catch {
      setValidationError("開始日時を正しい形式で入力してください。");
      return;
    }
    if (targetEpochMs <= nowEpochMs) {
      setValidationError("開始日時は現在時刻より後に設定してください。");
      return;
    }
    if (
      enabledSlides.includes("participation")
      && targetEpochMs - nowEpochMs > MAX_PROJECTION_REFRESH_DURATION_MS
    ) {
      setValidationError(
        "参加状況を表示する場合、開始日時は現在から12時間以内に設定してください。",
      );
      return;
    }

    const normalizedAnnouncements = announcements.map((announcement) =>
      announcement.trim(),
    );
    if (
      normalizedAnnouncements.length > MAX_ANNOUNCEMENTS ||
      normalizedAnnouncements.some(
        (announcement) =>
          announcement.length === 0 ||
          announcement.length > MAX_ANNOUNCEMENT_LENGTH,
      )
    ) {
      setValidationError(
        `お知らせは空欄にせず、各${MAX_ANNOUNCEMENT_LENGTH}文字以内で入力してください。`,
      );
      return;
    }

    setValidationError(null);
    onComplete({
      version: 3,
      startAtJst,
      apiId,
      enabledSlides,
      announcements: normalizedAnnouncements,
      countdownSoundEnabled,
      requestFullscreen,
    });
  };

  return (
    <div className="settings-backdrop" data-testid="settings-dialog">
      <dialog ref={dialogRef} className="settings-dialog" aria-labelledby="settings-title">
        <form onSubmit={handleSubmit}>
          <header className="settings-heading">
            <div>
              <p className="settings-kicker">SCREEN SETUP</p>
              <h1 id="settings-title">投影設定</h1>
            </div>
            <div className="settings-live-time">
              <time dateTime={new Date(nowEpochMs).toISOString()}>
                {formatJstTime(nowEpochMs)} <small>JST</small>
              </time>
              <div
                className={`sync-pill ${clockStatus.synchronized ? "sync-pill--ok" : ""}`}
                title="時刻同期状態"
              >
                <span aria-hidden="true" />
                {clockStatus.synchronized ? "基準時刻 同期済み" : "端末時刻で準備中"}
              </div>
            </div>
          </header>

          {systemNotices.length > 0 ? (
            <div
              className="settings-system-notice"
              role="alert"
              data-testid="system-notice"
            >
              <strong>確認が必要な状態です</strong>
              <ul>
                {systemNotices.map((notice) => <li key={notice}>{notice}</li>)}
              </ul>
              {showParticipationRefreshAction
                && enabledSlides.includes("participation") ? (
                  <button
                    type="button"
                    className="button button--secondary participation-refresh-button"
                    data-testid="participation-refresh-button"
                    disabled={participationRefreshing || !manualRefreshTargetIsValid}
                    onClick={() => {
                      if (manualRefreshTargetEpochMs !== null) {
                        onRefreshParticipation(apiId, manualRefreshTargetEpochMs);
                      }
                    }}
                  >
                    {participationRefreshing
                      ? "参加状況を取得中…"
                      : "参加状況を手動更新"}
                  </button>
                ) : null}
            </div>
          ) : null}

          <p className="settings-lead">
            開始前に内容を確認してください。この画面はリロードのたびに表示され、保存値は初期入力としてのみ使われます。
          </p>

          <div className="settings-grid">
            <section className="settings-card" aria-labelledby="time-settings-title">
              <div className="settings-card-number">01</div>
              <div>
                <h2 id="time-settings-title">開始時刻</h2>
                <p>カウントダウンで使う日本標準時（JST）を指定します。</p>
              </div>
              <label className="field field--full">
                <span>イベント開始日時（JST）</span>
                <input
                  type="datetime-local"
                  step="1"
                  value={startAtJst}
                  onChange={(event) => setStartAtJst(event.currentTarget.value)}
                  required
                />
              </label>
              <p className="field-note field--full">{targetSummary}</p>
            </section>

            <section className="settings-card" aria-labelledby="api-settings-title">
              <div className="settings-card-number">02</div>
              <div>
                <h2 id="api-settings-title">参加状況の対象</h2>
                <p>03と04は合算せず、選択した一方だけを表示します。参加率は座席未割当を含む予約者全員を対象に集計します。参加状況とカウントダウンを有効にして投影を開始すると、最新データを取得します。</p>
              </div>
              <label className="field field--full">
                <span>ReMaSys API</span>
                <select
                  value={apiId}
                  onChange={(event) => {
                    const nextApiId = event.currentTarget.value as "03" | "04";
                    setApiId(nextApiId);
                    onApiIdChange(nextApiId);
                  }}
                >
                  <option value="03">03 — 前夜祭</option>
                  <option value="04">04 — 後夜祭</option>
                </select>
              </label>
            </section>

            <section className="settings-card settings-card--slides" aria-labelledby="slide-settings-title">
              <div className="settings-card-number">03</div>
              <div>
                <h2 id="slide-settings-title">表示するスライド</h2>
                <p>有効な内容を10秒ずつ表示します。1つだけなら画面は移動しません。</p>
              </div>
              <div className="slide-options field--full">
                {orderedDefinitions.map((slide) => {
                  const orderIndex = enabledSlides.indexOf(slide.id);
                  const isEnabled = orderIndex >= 0;
                  return (
                  <div
                    className={`slide-option ${enabledSlides.includes(slide.id) ? "slide-option--checked" : ""}`}
                    key={slide.id}
                  >
                    <label>
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => toggleSlide(slide.id)}
                      />
                      <span className="slide-option-check" aria-hidden="true">✓</span>
                      <span>
                        <strong>{isEnabled ? `${orderIndex + 1}. ${slide.label}` : slide.label}</strong>
                        <small>{slide.description}</small>
                      </span>
                    </label>
                    {isEnabled && enabledSlides.length > 1 ? (
                      <span className="slide-order-buttons">
                        <button
                          type="button"
                          aria-label={`${slide.label}を1つ前へ`}
                          disabled={orderIndex === 0}
                          onClick={() => moveSlide(slide.id, -1)}
                        >↑</button>
                        <button
                          type="button"
                          aria-label={`${slide.label}を1つ後へ`}
                          disabled={orderIndex === enabledSlides.length - 1}
                          onClick={() => moveSlide(slide.id, 1)}
                        >↓</button>
                      </span>
                    ) : null}
                  </div>
                  );
                })}
              </div>
            </section>

            <section className="settings-card settings-card--announcements" aria-labelledby="announcement-settings-title">
              <div className="settings-card-number">04</div>
              <div>
                <h2 id="announcement-settings-title">お知らせ編集</h2>
                <p>画面下部を流す文章を最大{MAX_ANNOUNCEMENTS}件まで設定できます。</p>
              </div>
              <div className="announcement-editor field--full">
                {announcements.map((announcement, index) => (
                  <div className="announcement-editor-row" key={index}>
                    <label>
                      <span>お知らせ {index + 1}</span>
                      <input
                        type="text"
                        value={announcement}
                        maxLength={MAX_ANNOUNCEMENT_LENGTH}
                        onChange={(event) =>
                          updateAnnouncement(index, event.currentTarget.value)
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="announcement-remove"
                      aria-label={`お知らせ ${index + 1} を削除`}
                      onClick={() => removeAnnouncement(index)}
                    >
                      削除
                    </button>
                  </div>
                ))}
                {announcements.length === 0 ? (
                  <p className="announcement-empty">お知らせは表示されません。</p>
                ) : null}
                <div className="announcement-editor-footer">
                  <span>{announcements.length} / {MAX_ANNOUNCEMENTS}件</span>
                  <button
                    type="button"
                    className="announcement-add"
                    disabled={announcements.length >= MAX_ANNOUNCEMENTS}
                    onClick={addAnnouncement}
                  >
                    ＋ お知らせを追加
                  </button>
                </div>
              </div>
            </section>
          </div>

          <div className="settings-options">
            <label>
              <input
                type="checkbox"
                checked={requestFullscreen}
                onChange={(event) => setRequestFullscreen(event.currentTarget.checked)}
              />
              <span>
                <strong>全画面表示を開始</strong>
                <small>端末によっては手動操作が必要です</small>
              </span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={countdownSoundEnabled}
                disabled={!enabledSlides.includes("countdown")}
                onChange={(event) =>
                  setCountdownSoundEnabled(event.currentTarget.checked)
                }
              />
              <span>
                <strong>カウントダウン効果音</strong>
                <small>
                  10秒前から電子音を再生します。設定完了の操作後に有効になります
                </small>
              </span>
            </label>
          </div>

          {validationError ? (
            <p className="form-error" role="alert">{validationError}</p>
          ) : null}

          <footer className="settings-actions">
            {canCancel ? (
              <button className="button button--secondary" type="button" onClick={onCancel}>
                表示に戻る
              </button>
            ) : null}
            <button
              className="button button--primary"
              type="submit"
              disabled={!startAtIsFuture}
            >
              設定を完了して投影を開始
              <span aria-hidden="true">→</span>
            </button>
          </footer>
        </form>
      </dialog>
    </div>
  );
}
